import {
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Text,
} from "@chakra-ui/react"
import { Link, Route, Routes, useLocation } from "react-router-dom"
import {
  HamburgerIcon,
  RepeatIcon,
  DeleteIcon,
  ViewIcon,
} from "@chakra-ui/icons"
import GameSlidePage from "./pages/GameSlidePage"
import { resetGame } from "./features/game/gameSlice"
import { useAppDispatch } from "./app/hooks"
// import { MenuCharacterTextEditor } from "./features/character/CharacterText"
import { MenuCharacterAvatarEditor } from "./features/character/CharacterAvatarEditor"
import { resetPersistedState } from "./app/store"
import * as Sentry from "@sentry/react"
import ReviewPage from "./app/review/page"
import PasswordPage from "./pages/PasswordPage"
import ProtectedRoute from "./auth/ProtectedRoute"

// This version should match the APP_VERSION in store.ts
const APP_VERSION = "1.1.0.2"

// Create a custom fallback component for error boundaries
const ErrorFallback = ({ componentName }: { componentName: string }) => (
  <Box p={4} bg="red.50" color="red.500" borderRadius="md">
    <Text fontWeight="bold">Something went wrong in {componentName}</Text>
    <Text fontSize="sm">We've been notified and are working on a fix</Text>
  </Box>
)

const App = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()

  const onReset = () => {
    dispatch(resetGame())
  }

  const onPurgeStorage = () => {
    resetPersistedState()
    window.location.reload()
  }

  // Determine if the menu should be shown (not on password page)
  const showMenu = location.pathname !== "/password"

  return (
    <Box position="relative" minH="100vh">
      {/* Navigation Menu - Conditionally render */}
      {showMenu && (
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<HamburgerIcon />}
            variant="outline"
            size="sm"
            m={2}
            position="fixed"
            top={0}
            left={0}
            zIndex={1000}
            bg="whiteAlpha.900"
            _hover={{ bg: "whiteAlpha.800" }}
          />
          <MenuList>
            <Sentry.ErrorBoundary
              fallback={
                <ErrorFallback componentName="Character Avatar Editor" />
              }
            >
              <MenuCharacterAvatarEditor />
            </Sentry.ErrorBoundary>
            <MenuItem as={Link} to="/review">
              <ViewIcon mr={2} /> Review All Slides
            </MenuItem>
            <MenuItem onClick={onReset}>
              <RepeatIcon mr={2} /> Restart Game
            </MenuItem>
            <MenuItem onClick={onPurgeStorage}>
              <DeleteIcon mr={2} /> Clear All Data
            </MenuItem>
          </MenuList>
        </Menu>
      )}

      {/* Container for page content */}
      <Box minH="100%">
        <Sentry.ErrorBoundary
          fallback={<ErrorFallback componentName="Main Content Area" />}
        >
          {/* Routes - Updated structure */}
          <Routes>
            <Route path="/password" element={<PasswordPage />} />
            <Route element={<ProtectedRoute />}>
              {/* Protected Routes below */}
              <Route path="/" element={<GameSlidePage />} />
              <Route path="/review" element={<ReviewPage />} />
            </Route>
          </Routes>
        </Sentry.ErrorBoundary>
      </Box>

      {/* Version indicator - Conditionally render */}
      {showMenu && (
        <Box>
          <Text
            position="fixed"
            bottom={2}
            right={2}
            fontSize="xs"
            color="whiteAlpha.700"
            bg="blackAlpha.300"
            px={2}
            py={1}
            borderRadius="md"
            zIndex={1000}
          >
            v{APP_VERSION}
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default App
